import { AlertTriangle, Lock, RefreshCw, SearchX, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../../api/errors';
import { useCopy } from '../../lib/hooks';
import { cn } from '../../lib/cn';
import { Button } from './Button';

/** Порожній стан: пояснює, чому порожньо, і пропонує наступну дію. */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  compact,
  flush,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  compact?: boolean;
  /**
   * Усередині картки чи віджета, що вже має поля й заголовок: без бічних відступів, дрібніший заголовок
   * (не перебиває заголовок картки). Відступи через `className` не перекрити — `cn` не зливає класи.
   */
  flush?: boolean;
}) {
  return (
    <div className={cn('flex flex-col items-start', flush ? 'gap-1 py-3' : compact ? 'gap-2 px-4 py-6' : 'gap-2 px-6 py-12 sm:px-10', className)}>
      {icon && <span className="mb-1 flex size-9 items-center justify-center rounded-md border border-line bg-surface-2 text-fg-3">{icon}</span>}
      <h3 className={cn('font-semibold text-fg', flush ? 'text-body' : 'text-lead')}>{title}</h3>
      {description && <p className={cn('max-w-[56ch] text-fg-2', flush ? 'text-meta' : 'text-body')}>{description}</p>}
      {action && <div className="mt-2 flex flex-wrap gap-2">{action}</div>}
    </div>
  );
}

/** Помилка завантаження: текст сервера (problem+json), ідентифікатор запиту, повтор. */
export function ErrorState({ error, onRetry, className, compact }: { error: unknown; onRetry?: () => void; className?: string; compact?: boolean }) {
  const [copied, copy] = useCopy();
  const e = error instanceof ApiError ? error : null;
  const status = e?.status ?? -1;
  const icon =
    status === 0 ? <WifiOff className="size-4" aria-hidden /> : status === 403 ? <Lock className="size-4" aria-hidden /> : status === 404 ? <SearchX className="size-4" aria-hidden /> : <AlertTriangle className="size-4" aria-hidden />;
  const title =
    status === 0
      ? 'Немає з’єднання із сервером'
      : status === 403
        ? 'Недостатньо прав'
        : status === 404
          ? 'Не знайдено'
          : status === 401
            ? 'Сеанс завершено'
            : 'Не вдалося завантажити дані';
  const detail = e?.detail ?? (error instanceof Error ? error.message : 'Сталася помилка.');
  return (
    <div role="alert" className={cn('flex flex-col items-start gap-2', compact ? 'px-4 py-5' : 'px-6 py-10 sm:px-10', className)}>
      <span className="mb-1 flex size-9 items-center justify-center rounded-md border border-danger-line bg-danger-soft text-danger">{icon}</span>
      <h3 className="text-lead font-semibold text-fg">{title}</h3>
      <p className="max-w-[60ch] text-body text-fg-2">{detail}</p>
      {e?.requestId && (
        <button type="button" onClick={() => void copy(e.requestId!)} className="text-caption text-fg-3 hover:text-fg-2">
          Код запиту для підтримки: <span className="key">{e.requestId}</span> {copied ? '(скопійовано)' : ''}
        </button>
      )}
      {onRetry && status !== 403 && status !== 404 && (
        <Button size="sm" className="mt-1" icon={<RefreshCw className="size-3.5" aria-hidden />} onClick={onRetry}>
          Спробувати ще раз
        </Button>
      )}
    </div>
  );
}

/**
 * Заголовок сторінки (h1, 20 px) з діями праворуч.
 * `context` — «біжучий рядок» над заголовком лише для навігації чи місця: шлях («Зустрічі › М-12»), дата «Моєї роботи».
 * Пояснення чи зведення («Непрочитаних: 3», про що розділ) — `description` під заголовком, а не надзаголовком.
 */
export function PageHeader({
  title,
  context,
  description,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  context?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('border-b border-line bg-surface px-4 pt-4 sm:px-6', !children && 'pb-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          {context && <div className="mb-0.5 text-meta text-fg-3">{context}</div>}
          <h1 className="truncate text-heading font-semibold tracking-[-0.01em] text-fg">{title}</h1>
          {description && <p className="mt-0.5 max-w-[80ch] text-meta text-fg-2">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}
